import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ReactGoogleAutocomplete from "react-google-autocomplete";

/* The edit chat is the registration chat's twin, so it wears the same skin and
   speaks from the same string table rather than owning copies of either. */
import "../RegisterChat/RegisterChat.css";
import STRINGS from "../RegisterChat/chatLang";
import {
  SOCIAL_FIELDS,
  SOCIAL_STEPS,
  emptyLimits,
  readPlanFeatures,
  capReached,
  stepAllowedFor,
} from "../RegisterChat/planFeatures";
import {
  API_KEY,
  STATE_ID,
  MAX_IMAGE_MB,
  nowTime,
  onlyDigits,
  normalizeMobile,
  normalizeLink,
  looksLikeLink,
  compressImage,
  reverseGeocode,
} from "../RegisterChat/chatHelpers";
import useCommonQuery from "../../store/common/useCommonQuery";
import {
  getMyBussiness,
  getBusinessDetails,
  editBussiness,
  getCurrentPlan,
} from "../../store/Bussiness/bussinessUrl";
import {
  addCatelogue,
  deleteCatelogue,
  addCover,
  deleteCover,
} from "../../store/Images/imagesUrls";
import { AddService, deleteService } from "../../store/Service/serviceUrls";
import { uploadFileToS3 } from "../../utils/s3Upload";

/* Only the language choice is remembered. Every answer here is written to the
   server the moment it is given, so unlike registration there is no draft to
   restore — and no half-finished state that a stale draft could resurrect. */
const LANG_KEY = "mc_edit_chat_lang";

/* Fields that go through PATCH /business/my_business/<id>/. Catalogue photos,
   cover photos and services have their own endpoints and are handled apart. */
const PATCH_STEPS = new Set([
  "bizName",
  "type",
  "category",
  "subCategory",
  "location",
  "district",
  "postOffice",
  "pincode",
  "about",
  "logo",
  "banner",
  ...SOCIAL_STEPS,
]);

/* On a basic plan the backend swaps in BasicSubScriptionBusinessSerializer,
   which accepts only these fields; anything else is dropped without an error.
   Offering them would mean reporting "Saved" after changing nothing. Location
   is left out on purpose: that serializer takes location_info but neither
   latitude nor longitude, so the label would move and the pin would not. */
const BASIC_PLAN_STEPS = new Set([
  "bizName",
  "type",
  "category",
  "district",
  "postOffice",
  "pincode",
  "logo",
]);

/* Steps answered by typing. */
const TEXT_STEPS = new Set([
  "bizName",
  "postOffice",
  "pincode",
  "about",
  "catalogueTitle",
  "serviceName",
  ...SOCIAL_STEPS,
]);

/* Steps that accept an empty answer as "clear this". The social links are
   deliberately absent: BusinessUpdateSerializer declares them as URLField and
   CharField without allow_blank, so "" comes back as a validation error. */
const CLEARABLE_STEPS = new Set(["postOffice", "pincode", "about"]);

const IMAGE_STEPS = new Set(["logo", "banner", "cataloguePhoto", "coverPhoto"]);

/* Which card a step returns to once its answer is saved. */
const RETURN_TO = {
  cataloguePhoto: "catalogueMenu",
  catalogueTitle: "catalogueMenu",
  coverPhoto: "coverMenu",
  serviceName: "serviceMenu",
};

const EditChat = () => {
  const history = useHistory();
  const location = useLocation();

  /* The upgrade chat hands over the fields a new plan just unlocked, so the
     owner fills them in one run instead of hunting for them in the menu.
     Anything the current plan doesn't actually allow is dropped later, once
     the plan has been read. */
  const focusQueueRef = useRef(
    Array.isArray(location.state?.focus) ? [...location.state.focus] : []
  );

  const [lang, setLang] = useState(() =>
    localStorage.getItem(LANG_KEY) === "en" ? "en" : "ml"
  );
  /* A hand-edited or corrupt saved language must not blank the whole page. */
  const t = STRINGS[lang] || STRINGS.ml;

  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState("loading");
  const [biz, setBiz] = useState(null);
  const [limits, setLimits] = useState(emptyLimits);

  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  const [sheetSearch, setSheetSearch] = useState("");
  const [pendingPhoto, setPendingPhoto] = useState("");
  /* A pending removal: { kind, id, name }. Held in state so the composer can
     ask for a confirmation before anything is deleted. */
  const [confirmTarget, setConfirmTarget] = useState(null);

  const threadRef = useRef(null);
  const fileRef = useRef(null);
  /* The first visit to the menu is greeted differently from every return. */
  const menuSeenRef = useRef(false);

  /* Uploads and saves take seconds. Async callbacks read these refs rather
     than the state they closed over, which by then can be a step behind. */
  const stepRef = useRef(step);
  const bizRef = useRef(biz);
  const limitsRef = useRef(limits);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);
  useEffect(() => {
    bizRef.current = biz;
  }, [biz]);
  useEffect(() => {
    limitsRef.current = limits;
  }, [limits]);

  useEffect(() => {
    try {
      localStorage.setItem(LANG_KEY, lang);
    } catch (e) {
      /* Private mode — the toggle still works for this visit. */
    }
  }, [lang]);

  const { data: categoryList, refetch: refetchCategories } =
    useCommonQuery.Get_category({ search: "", limit: 2000, offset: 0 });
  const { data: subCategoryList, refetch: refetchSubCategories } =
    useCommonQuery.Get_subcategory({
      id: biz?.category,
      search: "",
      limit: 2000,
      offset: 0,
    });
  const { data: districtList, refetch: refetchDistricts } =
    useCommonQuery.Get_district(STATE_ID);

  /* ── Message helpers ── */

  const pushMsg = useCallback((msg) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        ts: nowTime(),
        ...msg,
      },
    ]);
  }, []);

  const pushBot = useCallback(
    (text, delay = 400) => {
      if (!text) return;
      setTyping(true);
      window.setTimeout(() => {
        setTyping(false);
        pushMsg({ from: "bot", text });
      }, delay);
    },
    [pushMsg]
  );

  const pushMe = useCallback((text) => pushMsg({ from: "me", text }), [pushMsg]);

  const pushError = useCallback(
    (text) => pushMsg({ from: "bot", text, error: true }),
    [pushMsg]
  );

  /* Every error the API can produce arrives as {status:"err", message:"…"},
     and the interceptor rethrows the response itself rather than the axios
     error, so the body sits directly on `.data`. */
  const apiMessage = useCallback(
    (err) =>
      err?.data?.message ||
      err?.response?.data?.message ||
      err?.data?.detail ||
      "",
    []
  );

  /* ── Plan gating ── */

  /* Read through the ref: the save and upload paths below run long after the
     render that closed over `limits`. */
  const canEdit = useCallback((key) => {
    const l = limitsRef.current;
    /* Hidden by the plan — the business page would not display it anyway. */
    if (!stepAllowedFor(l, key)) return false;
    /* Accepted by the API, but silently ignored on a basic plan. */
    if (l.isBasic && PATCH_STEPS.has(key) && !BASIC_PLAN_STEPS.has(key)) {
      return false;
    }
    /* The links card is only worth opening if at least one link inside it can
       be changed. A basic plan blocks every one of them, so the row itself has
       to go — otherwise tapping it would open an empty card. */
    if (key === "socialMenu") {
      return SOCIAL_STEPS.some(
        (id) =>
          stepAllowedFor(l, id) &&
          !(l.isBasic && PATCH_STEPS.has(id) && !BASIC_PLAN_STEPS.has(id))
      );
    }
    return true;
  }, []);

  /* loadAll filters the handed-over list, and it runs from an effect where the
     rendered closure may be a step behind. */
  const canEditRef = useRef(canEdit);
  useEffect(() => {
    canEditRef.current = canEdit;
  }, [canEdit]);

  /* ── Loading ── */

  /* Re-reads the business and hands the fresh copy back, so a caller can check
     that its add or delete actually landed. */
  const refreshBiz = useCallback(async () => {
    const id = bizRef.current?.id;
    if (!id) return null;
    const res = await getBusinessDetails(id);
    const fresh = res?.data;
    if (!fresh) return null;
    bizRef.current = fresh;
    setBiz(fresh);
    return fresh;
  }, []);

  const loadPlan = useCallback(async (businessId) => {
    try {
      const res = await getCurrentPlan(businessId);
      const plan = res?.data?.selected_plan;
      /* selected_plan is nullable, and when it is null the API leaves it as a
         bare id rather than an object — either way there is nothing to read. */
      if (!plan || typeof plan !== "object") return;
      const next = readPlanFeatures(plan.subscription_features, {
        isBasic: Boolean(plan.is_basic),
      });
      /* No feature row configured tells us nothing; leaving the limits alone
         keeps every field editable rather than hiding the whole menu. */
      if (!next) return;
      limitsRef.current = next;
      setLimits(next);
    } catch (e) {
      /* Without the plan every field stays on offer — better than an empty
         menu because one request failed. */
    }
  }, []);

  const loadAll = useCallback(async () => {
    setStep("loading");
    pushMsg({ from: "bot", text: t.editGreet });
    if (!localStorage.getItem("accessToken")) {
      setStep("noAuth");
      pushBot(t.noAuth, 400);
      return;
    }
    pushBot(t.loadingBiz, 300);
    try {
      const mine = await getMyBussiness();
      const first = mine?.data?.results?.[0];
      if (!first) {
        setStep("noBusiness");
        pushBot(t.noBusiness, 500);
        return;
      }
      const res = await getBusinessDetails(first.id);
      const detail = res?.data;
      /* The interceptor resolves undefined for 401/403/404 and network faults
         instead of rejecting, so an empty body is a failure, not an empty
         business. */
      if (!detail) throw new Error("no-response");
      bizRef.current = detail;
      setBiz(detail);
      await loadPlan(detail.id);
      /* Drop anything the plan turns out not to allow — the upgrade chat
         builds the list from the plan it just bought, but the activation is
         asynchronous and could still be catching up. */
      focusQueueRef.current = focusQueueRef.current.filter((id) =>
        canEditRef.current(id)
      );
      if (focusQueueRef.current.length) {
        pushBot(t.focusIntro, 400);
        window.setTimeout(() => nextFocusRef.current(), 900);
        return;
      }
      window.setTimeout(() => goToRef.current("menu"), 500);
    } catch (err) {
      setStep("loadFailed");
      pushError(apiMessage(err) || t.loadFailed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiMessage, loadPlan, pushBot, pushError, pushMsg, t]);

  /* ── Cards and navigation ── */

  /* Exactly one card lives in the thread at a time. Rebuilding it on every
     move keeps its Edit and Remove buttons pointing at current data — an older
     copy left further up would still be clickable and act on stale ids. */
  const showCard = useCallback(
    (kind, intro, delay = 380) => {
      setTyping(true);
      window.setTimeout(() => {
        setTyping(false);
        const stamp = Date.now();
        setMessages((prev) => [
          ...prev.filter((m) => !m.card && !m.cardIntro),
          {
            id: `ci_${stamp}`,
            ts: nowTime(),
            from: "bot",
            text: intro,
            cardIntro: true,
          },
          { id: `cd_${stamp}`, ts: nowTime(), from: "bot", card: kind },
        ]);
      }, delay);
    },
    []
  );

  const questionFor = useCallback(
    (id) => {
      switch (id) {
        case "bizName": return t.askBizName;
        case "type": return t.askType;
        case "category": return t.askCategory;
        case "subCategory": return t.askSubCategory;
        case "location": return t.askLocation;
        case "district": return t.askDistrict;
        case "postOffice": return t.askPostOffice;
        case "pincode": return t.askPincode;
        case "about": return t.askAbout;
        case "logo": return t.askLogo;
        case "banner": return t.askBanner;
        case "cataloguePhoto": return t.sendPhoto;
        case "catalogueTitle": return t.askCatalogueTitle;
        case "coverPhoto": return t.sendPhoto;
        case "serviceName": return t.askServices;
        case "socialWhatsapp": return t.askWhatsapp;
        case "socialFacebook": return t.askFacebook;
        case "socialInstagram": return t.askInstagram;
        case "socialYoutube": return t.askYoutube;
        case "socialWebsite": return t.askWebsite;
        case "socialGoogleReview": return t.askGoogleReview;
        case "socialGoogleLocation": return t.askGoogleLocation;
        default: return "";
      }
    },
    [t]
  );

  /* What the field holds today, so the visitor can see what they're replacing
     instead of having to remember it. */
  const currentValueFor = useCallback(
    (id) => {
      const b = bizRef.current || {};
      switch (id) {
        case "bizName": return b.name;
        case "type": return b.type === "shop" ? t.typeShop : t.typeService;
        case "category": return b.category_name;
        case "subCategory": return b.sub_category_name;
        case "location": return b.location_info;
        case "district": return b.district_name;
        case "postOffice": return b.post_office;
        case "pincode": return b.pin_code ? String(b.pin_code) : "";
        case "about": return b.about_us;
        case "logo": return b.image ? "🖼" : "";
        case "banner": return b.banner_image ? "🖼" : "";
        default:
          return SOCIAL_FIELDS[id] ? b[SOCIAL_FIELDS[id]] : "";
      }
    },
    [t]
  );

  const goTo = useCallback(
    (id, delay = 400) => {
      setStep(id);
      setDraft("");
      setSheetSearch("");
      setConfirmTarget(null);

      if (id === "menu") {
        const intro = menuSeenRef.current ? t.menuAgain : t.menuIntro;
        menuSeenRef.current = true;
        showCard("menu", intro, delay);
        return;
      }
      if (id === "socialMenu") return showCard("socialMenu", t.socialMenuIntro, delay);
      if (id === "catalogueMenu") return showCard("catalogueMenu", t.catalogueMenuIntro, delay);
      if (id === "coverMenu") return showCard("coverMenu", t.coverMenuIntro, delay);
      if (id === "serviceMenu") return showCard("serviceMenu", t.serviceMenuIntro, delay);
      if (id === "done") return;

      /* One bubble carries both the question and the current value; two would
         mean two typing indicators for a single prompt. */
      const question = questionFor(id);
      if (!question) return;
      const current = currentValueFor(id);
      /* Steps that add a new entry have nothing to replace, so showing
         "you haven't set this yet" under them would only confuse. */
      const adding =
        id === "cataloguePhoto" ||
        id === "catalogueTitle" ||
        id === "coverPhoto" ||
        id === "serviceName";
      const suffix = adding
        ? ""
        : `\n\n${current ? t.current(current) : t.currentNone}`;
      pushBot(`${question}${suffix}`, delay);
    },
    [currentValueFor, pushBot, questionFor, showCard, t]
  );

  /* loadAll runs before goTo is defined on the first render, so it reaches it
     through a ref rather than capturing an undefined binding. */
  const goToRef = useRef(goTo);
  useEffect(() => {
    goToRef.current = goTo;
  }, [goTo]);

  /* Pull the next handed-over field, or fall through to the menu once the
     list is done. */
  const nextFocus = useCallback(() => {
    const id = focusQueueRef.current.shift();
    if (!id) {
      pushBot(t.focusDone, 300);
      window.setTimeout(() => goToRef.current("menu"), 800);
      return;
    }
    goToRef.current(id, 350);
  }, [pushBot, t]);

  const nextFocusRef = useRef(nextFocus);
  useEffect(() => {
    nextFocusRef.current = nextFocus;
  }, [nextFocus]);

  /* Where an answer returns to. While a handed-over list is still running,
     finishing one field moves to the next instead of back to a menu. */
  const returnTarget = useCallback(
    (id) => (SOCIAL_FIELDS[id] ? "socialMenu" : RETURN_TO[id] || "menu"),
    []
  );

  /* ── Saving ── */

  /* Applies one PATCH and folds the same change into the local copy, so the
     card the visitor lands back on already shows the new value. */
  const applyPatch = useCallback(
    async (from, payload, localPatch) => {
      setBusy(true);
      try {
        const res = await editBussiness({ id: bizRef.current.id, ...payload });
        /* Undefined means the interceptor swallowed a 401/403/404 or a network
           error; without this the chat would report a save that never was. */
        if (!res) throw new Error("no-response");
        /* Re-read rather than trust the merge: the API drops any field the
           current plan's serializer doesn't accept without saying so, and only
           a refetch shows what actually changed. The merge is the fallback for
           when that read itself fails. */
        const fresh = await refreshBiz();
        if (!fresh) {
          const next = { ...bizRef.current, ...localPatch };
          bizRef.current = next;
          setBiz(next);
        }
        pushBot(t.saved, 260);
        window.setTimeout(() => {
          /* Mid hand-over: move on to the next unlocked field rather than
             dropping the owner back into a menu each time. */
          if (focusQueueRef.current.length) return nextFocusRef.current();
          goToRef.current(returnTarget(from));
        }, 850);
      } catch (err) {
        pushError(t.saveFailed(apiMessage(err)));
      } finally {
        setBusy(false);
      }
    },
    [apiMessage, pushBot, pushError, refreshBiz, returnTarget, t]
  );

  /* ── Text answers ── */

  const submitText = async () => {
    const value = draft.trim();
    const current = step;

    if (!TEXT_STEPS.has(current)) return;

    if (!value && !CLEARABLE_STEPS.has(current)) {
      /* A link cleared to nothing is rejected by the API, so say why rather
         than letting the visitor watch a save fail. */
      pushError(SOCIAL_FIELDS[current] ? t.linkCannotClear : t.errRequired);
      return;
    }
    if (current === "about" && value.length > 1000) {
      pushError(t.errTooLong);
      return;
    }
    if (current === "pincode" && value && onlyDigits(value).length !== 6) {
      pushError(t.errPincode);
      return;
    }

    switch (current) {
      case "bizName":
        pushMe(value);
        return applyPatch(current, { name: value }, { name: value });
      case "postOffice":
        pushMe(value || "—");
        return applyPatch(current, { post_office: value }, { post_office: value });
      case "pincode": {
        const digits = onlyDigits(value);
        pushMe(digits || "—");
        /* null, not "", clears a PositiveIntegerField. */
        const pin = digits ? Number(digits) : null;
        return applyPatch(current, { pin_code: pin }, { pin_code: pin });
      }
      case "about":
        pushMe(value || "—");
        return applyPatch(current, { about_us: value }, { about_us: value });
      case "catalogueTitle":
        pushMe(value);
        return addCatalogueItem(pendingPhoto, value);
      case "serviceName":
        pushMe(value);
        return addServiceItem(value);
      default: {
        const field = SOCIAL_FIELDS[current];
        if (!field) return;
        if (current === "socialWhatsapp") {
          const num = normalizeMobile(value);
          if (num.length < 10) {
            pushError(t.errMobile);
            return;
          }
          pushMe(num);
          return applyPatch(current, { [field]: num }, { [field]: num });
        }
        if (!looksLikeLink(value)) {
          pushError(t.errLink);
          return;
        }
        const link = normalizeLink(value);
        pushMe(link);
        return applyPatch(current, { [field]: link }, { [field]: link });
      }
    }
  };

  /* ── Choice answers ── */

  const chooseType = (value) => {
    pushMe(value === "shop" ? t.typeShop : t.typeService);
    applyPatch("type", { type: value }, { type: value });
  };

  const choosePick = (kind, item) => {
    pushMe(item.name);
    if (kind === "category") {
      /* A sub category belongs to its category, so the old one cannot survive
         the change — it is cleared in the same request. */
      applyPatch(
        "category",
        { category: item.id, sub_category: null },
        {
          category: item.id,
          category_name: item.name,
          sub_category: null,
          sub_category_name: "",
        }
      );
    } else if (kind === "subCategory") {
      applyPatch(
        "subCategory",
        { sub_category: item.id },
        { sub_category: item.id, sub_category_name: item.name }
      );
    } else {
      applyPatch(
        "district",
        { district: item.id },
        { district: item.id, district_name: item.name }
      );
    }
  };

  /* ── Location ── */

  const applyLocation = async (lat, lng, placeName) => {
    const geo = await reverseGeocode(lat, lng);
    const label =
      placeName ||
      geo.locality ||
      `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
    pushMsg({ from: "me", text: `📍 ${label}` });
    /* Only the pin and its label are touched here. District, post office and
       pincode are separate menu entries the owner has already set, and
       overwriting them from a rough geocode would undo deliberate edits. */
    await applyPatch(
      "location",
      { latitude: lat, longitude: lng, location_info: label },
      { latitude: lat, longitude: lng, location_info: label }
    );
  };

  const useGps = () => {
    if (!navigator.geolocation) {
      pushError(t.locationDenied);
      return;
    }
    setBusy(true);
    pushBot(t.locating, 150);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        /* Without the finally, a throw in here would leave the composer
           disabled with no way out but a reload. */
        try {
          await applyLocation(pos.coords.latitude, pos.coords.longitude, "");
        } catch (err) {
          pushError(t.locationDenied);
        } finally {
          setBusy(false);
        }
      },
      () => {
        setBusy(false);
        pushError(t.locationDenied);
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const onPlacePicked = async (place) => {
    if (!place?.geometry?.location) return;
    setBusy(true);
    try {
      await applyLocation(
        place.geometry.location.lat(),
        place.geometry.location.lng(),
        place.name || place.formatted_address || ""
      );
    } catch (err) {
      pushError(t.locationDenied);
    } finally {
      setBusy(false);
    }
  };

  /* ── Catalogue / cover / service lists ── */

  const addCatalogueItem = async (url, name) => {
    setBusy(true);
    try {
      await addCatelogue({
        id: bizRef.current.id,
        catalogue_image: url,
        name,
        is_active: true,
      });
      /* The POST response carries the new row, but a refetch is what proves it
         is really there — and it is also where the row's id comes from, which
         a later Remove needs. */
      const fresh = await refreshBiz();
      const saved = (fresh?.catalogue_images || []).some(
        (c) => c.catalogue_image === url
      );
      if (!saved) throw new Error("not-saved");
      setPendingPhoto("");
      pushBot(t.saved, 260);
      window.setTimeout(() => goToRef.current("catalogueMenu"), 850);
    } catch (err) {
      pushError(t.saveFailed(apiMessage(err)));
    } finally {
      setBusy(false);
    }
  };

  const addCoverItem = async (url) => {
    setBusy(true);
    try {
      await addCover({ id: bizRef.current.id, cover_image: url, is_active: true });
      const fresh = await refreshBiz();
      const saved = (fresh?.cover_images || []).some((c) => c.cover_image === url);
      if (!saved) throw new Error("not-saved");
      pushBot(t.saved, 260);
      window.setTimeout(() => goToRef.current("coverMenu"), 850);
    } catch (err) {
      pushError(t.saveFailed(apiMessage(err)));
    } finally {
      setBusy(false);
    }
  };

  const addServiceItem = async (name) => {
    setBusy(true);
    try {
      await AddService({ id: bizRef.current.id, name, is_active: true });
      const fresh = await refreshBiz();
      const saved = (fresh?.services || []).some((s) => s.name === name);
      if (!saved) throw new Error("not-saved");
      pushBot(t.saved, 260);
      window.setTimeout(() => goToRef.current("serviceMenu"), 850);
    } catch (err) {
      pushError(t.saveFailed(apiMessage(err)));
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async () => {
    const target = confirmTarget;
    if (!target) return;
    setConfirmTarget(null);
    pushMe(t.yes);
    setBusy(true);
    const businessId = bizRef.current.id;
    try {
      if (target.kind === "catalogue") {
        await deleteCatelogue({ businessId, id: target.id });
      } else if (target.kind === "cover") {
        await deleteCover({ businessId, id: target.id });
      } else {
        await deleteService({ business_id: businessId, id: target.id });
      }
      /* A successful delete answers 204 with no body, and the interceptor
         resolves undefined on 401/404 — neither can be told apart from the
         return value, so the refetch is the only honest proof. */
      const fresh = await refreshBiz();
      const listKey =
        target.kind === "catalogue"
          ? "catalogue_images"
          : target.kind === "cover"
          ? "cover_images"
          : "services";
      const stillThere = (fresh?.[listKey] || []).some((i) => i.id === target.id);
      if (stillThere) throw new Error("not-deleted");
      pushBot(t.removed, 260);
      /* Redraw the list under the confirmation rather than leaving the card
         stranded above it — showCard also drops the now-stale copy. */
      window.setTimeout(() => goToRef.current(stepRef.current), 800);
    } catch (err) {
      pushError(t.saveFailed(apiMessage(err)));
    } finally {
      setBusy(false);
    }
  };

  /* ── Images ── */

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_IMAGE_MB * 1024 * 1024 * 3) {
      /* Very large originals are still accepted — compression brings them
         down — but anything absurd is rejected up front. */
      pushError(t.errImage);
      return;
    }

    /* The step at pick time. An upload takes seconds and the visitor can move
       on meanwhile, so the result is only applied if we're still here. */
    const atStep = step;
    const localId = `up_${Date.now()}`;
    const preview = URL.createObjectURL(file);
    pushMsg({ from: "me", image: preview, uploading: true, localId });
    setBusy(true);

    try {
      const compressed = await compressImage(file);
      if (compressed.size > MAX_IMAGE_MB * 1024 * 1024) {
        throw new Error(t.errImage);
      }
      const url = await uploadFileToS3(compressed);
      setMessages((prev) =>
        prev.map((m) =>
          m.localId === localId ? { ...m, image: url, uploading: false } : m
        )
      );
      URL.revokeObjectURL(preview);

      if (stepRef.current !== atStep) return;

      switch (atStep) {
        case "logo":
          await applyPatch("logo", { image: url }, { image: url });
          break;
        case "banner":
          await applyPatch("banner", { banner_image: url }, { banner_image: url });
          break;
        case "cataloguePhoto":
          /* The photo is uploaded first and named second, so it waits here
             until the title arrives. */
          setPendingPhoto(url);
          goToRef.current("catalogueTitle");
          break;
        case "coverPhoto":
          await addCoverItem(url);
          break;
        default:
          break;
      }
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.localId !== localId));
      URL.revokeObjectURL(preview);
      /* s3Upload throws raw English internals; keep the chat in one language. */
      pushError(t.errNetwork);
    } finally {
      setBusy(false);
    }
  };

  /* ── Start ── */

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typing, step]);

  /* ── Derived ── */

  const sheetKind =
    step === "category"
      ? "category"
      : step === "subCategory"
      ? "subCategory"
      : step === "district"
      ? "district"
      : null;

  const sheetItems = useMemo(() => {
    let list = [];
    if (sheetKind === "category") list = categoryList?.data?.results || [];
    if (sheetKind === "subCategory") list = subCategoryList?.data?.results || [];
    if (sheetKind === "district") list = districtList?.data?.results || [];
    const q = sheetSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((i) => (i.name || "").toLowerCase().includes(q));
  }, [sheetKind, sheetSearch, categoryList, subCategoryList, districtList]);

  const menuRows = useMemo(() => {
    if (!biz) return [];
    return [
      { key: "bizName", label: t.labels.bizName, value: biz.name },
      {
        key: "type",
        label: t.labels.type,
        value: biz.type === "shop" ? t.typeShop : t.typeService,
      },
      { key: "category", label: t.labels.category, value: biz.category_name },
      { key: "subCategory", label: t.labels.subCategory, value: biz.sub_category_name },
      { key: "location", label: t.labels.location, value: biz.location_info },
      { key: "district", label: t.labels.district, value: biz.district_name },
      { key: "postOffice", label: t.labels.postOffice, value: biz.post_office },
      {
        key: "pincode",
        label: t.labels.pincode,
        value: biz.pin_code ? String(biz.pin_code) : "",
      },
      { key: "about", label: t.labels.about, value: biz.about_us },
      { key: "logo", label: t.labels.logo, images: biz.image ? [biz.image] : [] },
      {
        key: "banner",
        label: t.labels.banner,
        images: biz.banner_image ? [biz.banner_image] : [],
      },
      {
        key: "catalogueMenu",
        label: t.labels.catalogue,
        images: (biz.catalogue_images || []).map((c) => c.catalogue_image),
      },
      {
        key: "coverMenu",
        label: t.labels.cover,
        images: (biz.cover_images || []).map((c) => c.cover_image),
      },
      {
        key: "serviceMenu",
        label: t.labels.services,
        value: (biz.services || []).map((s) => s.name).join(", "),
      },
      {
        key: "socialMenu",
        label: t.labels.social,
        value: SOCIAL_STEPS.map((s) => biz[SOCIAL_FIELDS[s]])
          .filter(Boolean)
          .join("\n"),
      },
      /* The same gate that hides a question hides its row: a field this plan
         never displays is not something the owner can usefully change. */
    ].filter((row) => canEdit(row.key));
    /* `limits` is not read directly — it is here so the menu rebuilds once the
       plan lands and canEdit starts turning rows away. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biz, t, canEdit, limits]);

  const socialRows = useMemo(() => {
    if (!biz) return [];
    return SOCIAL_STEPS.filter(canEdit).map((s) => ({
      key: s,
      label: t.socialLabels[SOCIAL_FIELDS[s]],
      value: biz[SOCIAL_FIELDS[s]],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biz, t, canEdit, limits]);

  /* Is there room for one more photo / service on this plan? */
  const listFull = useCallback(
    (kind) => {
      const b = bizRef.current || {};
      if (kind === "catalogue") {
        return capReached(limits.catalogue, (b.catalogue_images || []).length);
      }
      if (kind === "cover") {
        return capReached(limits.cover, (b.cover_images || []).length);
      }
      return capReached(limits.service, (b.services || []).length);
    },
    [limits]
  );

  const limitMessage = (kind) => {
    if (kind === "catalogue") return t.catalogueLimit(limits.catalogue);
    if (kind === "cover") return t.coverLimit(limits.cover);
    return t.serviceLimit(limits.service);
  };

  /* Closing the chat clears the card away, so the last thing on screen is a
     confirmation rather than a menu that no longer does anything. */
  const finishEditing = () => {
    /* Leaving early abandons anything still queued, so it can't resurface
       later in the session. */
    focusQueueRef.current = [];
    setStep("done");
    setMessages((prev) => prev.filter((m) => !m.card && !m.cardIntro));
    pushBot(t.editDoneTitle, 250);
    window.setTimeout(() => pushMsg({ from: "bot", text: t.editDoneBody }), 800);
  };

  const startAdd = (kind) => {
    if (listFull(kind)) {
      pushBot(limitMessage(kind), 250);
      return;
    }
    if (kind === "catalogue") goTo("cataloguePhoto");
    else if (kind === "cover") goTo("coverPhoto");
    else goTo("serviceName");
  };

  /* ── Composer ── */

  const renderComposer = () => {
    if (step === "loading") return null;

    if (step === "noAuth") {
      return (
        <div className="rc-cta">
          <button className="solid" onClick={() => history.push("/sign-in")}>
            {t.signInBtn}
          </button>
        </div>
      );
    }

    if (step === "noBusiness") {
      return (
        <div className="rc-cta">
          <button className="solid" onClick={() => history.push("/register-chat")}>
            {t.registerBtn}
          </button>
        </div>
      );
    }

    if (step === "loadFailed") {
      return (
        <div className="rc-cta">
          <button
            className="solid"
            onClick={() => {
              setMessages([]);
              loadAll();
            }}
          >
            {t.retry}
          </button>
        </div>
      );
    }

    if (confirmTarget) {
      return (
        <div className="rc-chips">
          <button className="rc-chip primary" disabled={busy} onClick={removeItem}>
            {t.yes}
          </button>
          <button
            className="rc-chip ghost"
            disabled={busy}
            onClick={() => {
              setConfirmTarget(null);
              pushMe(t.no);
            }}
          >
            {t.no}
          </button>
        </div>
      );
    }

    if (step === "menu") {
      return (
        <div className="rc-cta">
          <button className="solid" onClick={finishEditing}>
            {t.finish}
          </button>
        </div>
      );
    }

    if (step === "done") {
      return (
        <div className="rc-cta">
          <button
            className="solid"
            onClick={() => history.push(`/product-details/${biz?.id}`)}
          >
            {t.previewBtn}
          </button>
          <button className="outline" onClick={() => history.push("/my-list")}>
            {t.myListingsBtn}
          </button>
        </div>
      );
    }

    if (step === "socialMenu") {
      return (
        <div className="rc-cta">
          <button className="solid" onClick={() => goTo("menu")}>
            {t.back}
          </button>
        </div>
      );
    }

    if (step === "catalogueMenu" || step === "coverMenu" || step === "serviceMenu") {
      const kind =
        step === "catalogueMenu"
          ? "catalogue"
          : step === "coverMenu"
          ? "cover"
          : "service";
      return (
        <div className="rc-chips">
          <button
            className="rc-chip primary"
            disabled={busy || listFull(kind)}
            onClick={() => startAdd(kind)}
          >
            {t.addNew}
          </button>
          <button className="rc-chip ghost" disabled={busy} onClick={() => goTo("menu")}>
            {t.back}
          </button>
        </div>
      );
    }

    if (step === "type") {
      return (
        <div className="rc-chips">
          <button className="rc-chip primary" disabled={busy} onClick={() => chooseType("shop")}>
            🏪 {t.typeShop}
          </button>
          <button className="rc-chip" disabled={busy} onClick={() => chooseType("service")}>
            🛠 {t.typeService}
          </button>
          <button className="rc-chip ghost" disabled={busy} onClick={() => goTo("menu")}>
            {t.back}
          </button>
        </div>
      );
    }

    if (sheetKind) {
      return (
        <>
          <div className="rc-sheet">
            <div className="rc-sheet-search">
              <input
                value={sheetSearch}
                onChange={(e) => setSheetSearch(e.target.value)}
                placeholder={t.search}
              />
            </div>
            <div className="rc-sheet-list">
              {sheetItems.length === 0 && (
                <div className="rc-sheet-empty">{t.noResults}</div>
              )}
              {sheetItems.map((item) => (
                <div
                  key={item.id}
                  className="rc-sheet-item"
                  onClick={() => !busy && choosePick(sheetKind, item)}
                >
                  {item.image ? <img src={item.image} alt="" /> : null}
                  <span>{item.name}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rc-chips">
            {/* A failed fetch would otherwise be a dead end with no way to ask
                for the list again. */}
            {sheetItems.length === 0 && !sheetSearch.trim() && (
              <button
                className="rc-chip"
                onClick={() => {
                  if (sheetKind === "category") refetchCategories();
                  else if (sheetKind === "subCategory") refetchSubCategories();
                  else refetchDistricts();
                }}
              >
                {t.retry}
              </button>
            )}
            <button className="rc-chip ghost" disabled={busy} onClick={() => goTo("menu")}>
              {t.back}
            </button>
          </div>
        </>
      );
    }

    if (step === "location") {
      return (
        <>
          <div className="rc-location-search">
            <ReactGoogleAutocomplete
              apiKey={API_KEY}
              options={{
                types: ["neighborhood", "locality", "sublocality", "postal_code", "point_of_interest"],
                componentRestrictions: { country: "in" },
                fields: ["address_components", "geometry", "name", "place_id", "formatted_address"],
              }}
              onPlaceSelected={onPlacePicked}
              placeholder={t.searchPlace}
            />
          </div>
          <div className="rc-chips">
            <button className="rc-chip primary block" disabled={busy} onClick={useGps}>
              <MyLocationIcon style={{ fontSize: 16, verticalAlign: "-3px", marginRight: 6 }} />
              {t.shareLocation}
            </button>
            <button className="rc-chip ghost" disabled={busy} onClick={() => goTo("menu")}>
              {t.back}
            </button>
          </div>
        </>
      );
    }

    if (IMAGE_STEPS.has(step)) {
      return (
        <div className="rc-chips">
          <button
            className="rc-chip primary"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            <PhotoCameraIcon style={{ fontSize: 16, verticalAlign: "-3px", marginRight: 6 }} />
            {busy ? t.uploading : t.addPhoto}
          </button>
          <button
            className="rc-chip ghost"
            disabled={busy}
            onClick={() => goTo(returnTarget(step))}
          >
            {t.back}
          </button>
        </div>
      );
    }

    /* Default: a text / number field */
    const numeric = step === "pincode" || step === "socialWhatsapp";
    const clearable = CLEARABLE_STEPS.has(step);
    return (
      <>
        <div className="rc-chips">
          <button
            className="rc-chip ghost"
            disabled={busy}
            onClick={() => goTo(returnTarget(step))}
          >
            {t.back}
          </button>
        </div>
        <div className="rc-input-row">
          <div className="rc-input-shell">
            <textarea
              className="rc-input"
              rows={1}
              value={draft}
              inputMode={numeric ? "numeric" : "text"}
              placeholder={t.typeHere}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!busy) submitText();
                }
              }}
            />
          </div>
          <button
            className="rc-send"
            disabled={busy || (!draft.trim() && !clearable)}
            onClick={submitText}
          >
            <SendIcon fontSize="small" />
          </button>
        </div>
      </>
    );
  };

  /* ── Card rendering ── */

  const renderRows = (rows) => (
    <div className="rc-review">
      {/* A plan restrictive enough to leave nothing editable would otherwise
          render an empty white box with no explanation. */}
      {rows.length === 0 && (
        <div className="rc-review-row">
          <div className="rc-review-value">
            <span className="empty">{t.planNote}</span>
          </div>
        </div>
      )}
      {rows.map((row) => (
        <div className="rc-review-row" key={row.key}>
          <div className="rc-review-label">{row.label}</div>
          <div className="rc-review-value">
            {row.images ? (
              row.images.length ? (
                <div className="rc-review-thumbs">
                  {row.images.map((src, k) => (
                    <img src={src} alt="" key={k} />
                  ))}
                </div>
              ) : (
                <span className="empty">—</span>
              )
            ) : row.value ? (
              row.value
            ) : (
              <span className="empty">—</span>
            )}
          </div>
          <button
            className="rc-review-edit"
            disabled={busy}
            onClick={() => goTo(row.key, 250)}
          >
            {t.edit}
          </button>
        </div>
      ))}
    </div>
  );

  /* Catalogue, cover and service cards list their rows one per entry, each
     with its own Remove — the main card only shows them as a summary. */
  const renderListCard = (kind) => {
    const b = biz || {};
    const items =
      kind === "catalogue"
        ? (b.catalogue_images || []).map((c) => ({
            id: c.id,
            name: c.name,
            image: c.catalogue_image,
          }))
        : kind === "cover"
        ? (b.cover_images || []).map((c) => ({ id: c.id, image: c.cover_image }))
        : (b.services || []).map((s) => ({ id: s.id, name: s.name }));

    if (!items.length) {
      return (
        <div className="rc-review">
          <div className="rc-review-row">
            <div className="rc-review-value">
              <span className="empty">{t.emptyList}</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="rc-review">
        {items.map((item) => (
          <div className="rc-review-row" key={item.id}>
            <div className="rc-review-value">
              {item.image ? (
                <div className="rc-review-thumbs">
                  <img src={item.image} alt="" />
                </div>
              ) : null}
              {item.name || (item.image ? "" : "—")}
            </div>
            <button
              className="rc-review-edit"
              disabled={busy}
              onClick={() => {
                setConfirmTarget({ kind, id: item.id, name: item.name });
                pushBot(t.confirmRemove(item.name), 200);
              }}
            >
              {t.remove}
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderCard = (kind) => {
    if (kind === "menu") return renderRows(menuRows);
    if (kind === "socialMenu") return renderRows(socialRows);
    return renderListCard(
      kind === "catalogueMenu" ? "catalogue" : kind === "coverMenu" ? "cover" : "service"
    );
  };

  /* ── Render ── */

  return (
    <div className="rc-root">
      <div className="rc-header">
        <button className="rc-back" onClick={() => history.push("/my-list")}>
          <ArrowBackIcon fontSize="small" />
        </button>
        <div className="rc-avatar">
          <StorefrontIcon fontSize="small" />
        </div>
        <div className="rc-header-text">
          <div className="rc-header-name">{biz?.name || t.headerName}</div>
          <div className="rc-header-status">
            {typing ? t.typing : t.headerStatus}
          </div>
        </div>
        <button
          className="rc-lang-toggle"
          onClick={() => setLang((l) => (l === "ml" ? "en" : "ml"))}
        >
          {lang === "ml" ? "EN" : "മല"}
        </button>
      </div>

      <div className="rc-thread" ref={threadRef}>
        <div className="rc-thread-inner">
          <div className="rc-day-pill">{t.today}</div>

          {messages.map((m, i) => {
            const prev = messages[i - 1];
            const tail = !prev || prev.from !== m.from;

            if (m.card) {
              return (
                <div className="rc-row" key={m.id}>
                  {renderCard(m.card)}
                </div>
              );
            }

            return (
              <div className={`rc-row ${m.from}`} key={m.id}>
                <div
                  className={`rc-bubble ${m.from} ${tail ? "tail" : ""} ${
                    m.error ? "error" : ""
                  }`}
                >
                  {m.image && (
                    <div className="rc-photo-wrap">
                      <img className="rc-photo" src={m.image} alt="" />
                      {m.uploading && (
                        <div className="rc-photo-progress">{t.uploading}</div>
                      )}
                    </div>
                  )}
                  {m.text}
                  <span className="rc-meta">
                    {m.ts}
                    {m.from === "me" && (
                      <DoneAllIcon className="rc-tick" style={{ fontSize: 14 }} />
                    )}
                  </span>
                </div>
              </div>
            );
          })}

          {typing && (
            <div className="rc-row">
              <div className="rc-bubble bot tail">
                <div className="rc-typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rc-composer">
        <div className="rc-composer-inner">{renderComposer()}</div>
      </div>

      <input
        ref={fileRef}
        className="rc-hidden-file"
        type="file"
        accept="image/*"
        onChange={onFilePicked}
      />
    </div>
  );
};

export default EditChat;
